import { useRef, useState } from 'react';
import { m, useScroll, useSpring, useTransform, useMotionValueEvent } from 'framer-motion';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import AvatarGroup from '@mui/material/AvatarGroup';
import Avatar, { avatarClasses } from '@mui/material/Avatar';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useResponsive } from 'src/hooks/use-responsive';

import { _mock } from 'src/_mock';
import { CONFIG } from 'src/config-global';
import { textGradient } from 'src/theme/styles';

import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import { varFade, MotionContainer } from 'src/components/animate';

import { HeroBackground } from './components/hero-background';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import ICON from 'src/utils/icon';
import axios, { endpoints } from "src/utils/axios";
import EnqueueSnackbar from "src/utils/notistack";

// ----------------------------------------------------------------------

const smKey = 'sm';
const mdKey = 'md';
const lgKey = 'lg';

export default function HomeHero({ sx, ...other }) {
  const theme = useTheme();

  const [email, setEmail] = useState("");

  const scroll = useScrollPercent();

  const mdUp = useResponsive('up', mdKey);

  const distance = mdUp ? scroll.percent : 0;

  const y1 = useTransformY(scroll.scrollY, distance * -7);
  const y2 = useTransformY(scroll.scrollY, distance * -6);
  const y3 = useTransformY(scroll.scrollY, distance * -5);
  const y4 = useTransformY(scroll.scrollY, distance * -4);
  const y5 = useTransformY(scroll.scrollY, distance * -3);

  const opacity = useTransform(
    scroll.scrollY,
    [0, 1],
    [1, mdUp ? Number((1 - scroll.percent / 100).toFixed(1)) : 1],
  );

  const renderHeading = (
    <MInview>
      <Box
        component="h1"
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        sx={{
          ...theme.typography.h2,
          my: 0,
          mx: 'auto',
          maxWidth: 1280,
          fontFamily: 'Lava Devanagari, sans-serif',
          fontWeight: 900,
        }}

      >
        <Box fontSize={64} component="span" sx={{ width: 1 }}>
          Turn your course materials
        </Box>
        <Stack direction={'row'} alignItems={'center'} flexWrap={{
          xs: 'wrap',
          lg: 'nowrap',

        }} justifyContent={'center'}>
          <Box fontSize={64} component="span" sx={{ width: 1 }}>
            into Study GPTs with
          </Box>
          <Box
            component={m.span}
            animate={{ backgroundPosition: '200% center' }}
            transition={{
              duration: 20,
              ease: 'linear',
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            sx={{
              color: '#22C55E',
              backgroundSize: '400%',
              ml: { xs: 0.75, md: 1, xl: 1.5 },
            }}
            fontSize={64}
          >
            Coursepals.AI
          </Box>
        </Stack>
      </Box>
    </MInview>
  );

  const renderText = (
    <MInview>
      <Typography
        variant="body2"
        sx={{
          mx: 'auto',
          [theme.breakpoints.up(smKey)]: { whiteSpace: 'pre' },
          [theme.breakpoints.up(lgKey)]: { fontSize: 20, lineHeight: '36px' },
        }}
      >
        {`Your all-in-one GenAI application for course learning, team \ncollaboration and academic research`}
      </Typography>
    </MInview>
  );

  const renderRatings = (
    <MInview>
      <Box
        gap={1.5}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="center"
        sx={{ typography: 'body2' }}
      >
        <AvatarGroup sx={{ [`& .${avatarClasses.root}`]: { width: 32, height: 32 } }}>
          {[...Array(3)].map((_, index) => (
            <Avatar
              key={index}
              src={`/assets/images/landing_page/demo_avatar_${index+1}.png`}
            />
          ))}
        </AvatarGroup>
        300+ Stanford Students & 20+ Professors are on the waitlist
      </Box>
    </MInview>
  );

  const handleSubmitWaitlist = async () => {
    if (!email) {
      EnqueueSnackbar("Please input your email", "error");
      return;
    }

    try {
      const payload = {
        email,
        waitlist_id: 19159,
      };

      const response = await axios.post(endpoints.waitlist.signup, payload);
      if (response.status === 200) {
        EnqueueSnackbar("Join waiting list success", "success");
      }
    } catch (error) {
      console.error(`There was an error when submitting the email: ${email}`, error);
      EnqueueSnackbar("Join waiting list failed", "error");
    }
  };

  const renderJoin = (
    <MInview>
      <Box
        gap={1.5}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="center"
        sx={{ typography: 'subtitle2' }}
      >
        <InputBase
          sx={{height:'54px',width:'450px',border:`1px solid ${theme.palette.divider}`,borderRadius:'8px',bgcolor:'#FFFFFF',pl:2}}
          placeholder={"your-email@example.com"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          endAdornment={
            <Button sx={{flexShrink:0,height:'48px',fontSize:18}} size={'small'} variant={'contained'} onClick={handleSubmitWaitlist}>
              Join Waitlist
            </Button>
          }
          // sx={{
          //   px: 1,
          //   borderBottom: theme => `solid 1px ${theme.palette.divider}`,
          // }}
        />
      </Box>
    </MInview>
  );

  const renderButtons = (
    <Box display="flex" flexWrap="wrap" justifyContent="center" gap={{ xs: 1.5, sm: 2 }}>
      <MInview>
        <Stack alignItems="center" spacing={2.5}>
          <Button
            component={RouterLink}
            href={paths.dashboard.root}
            color="inherit"
            size="large"
            variant="contained"
            startIcon={<Iconify width={24} icon="iconoir:flash" />}
          >
            <span>
              Live preview
              <Box
                component="small"
                sx={{
                  mt: '-3px',
                  opacity: 0.64,
                  display: 'flex',
                  fontSize: theme.typography.pxToRem(10),
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              >
                v{CONFIG.site.version}
              </Box>
            </span>
          </Button>

          <Link
            color="inherit"
            variant="body2"
            target="_blank"
            rel="noopener"
            href={paths.freeUI}
            underline="always"
            sx={{ gap: 0.5, alignItems: 'center', display: 'inline-flex' }}
          >
            Get free version
            <Iconify width={16} icon="eva:external-link-fill" />
          </Link>
        </Stack>
      </MInview>

      <MInview>
        <Button
          color="inherit"
          size="large"
          variant="outlined"
          target="_blank"
          rel="noopener"
          href={paths.figma}
          startIcon={<Iconify width={24} icon="solar:figma-outline" />}
          sx={{ borderColor: 'text.primary' }}
        >
          Figma preview
        </Button>
      </MInview>
    </Box>
  );

  const renderIcons = (
    <Stack spacing={3} sx={{ textAlign: 'center' }}>
      <MInview>
        <Typography variant="overline" sx={{ opacity: 0.4 }}>
          Available For
        </Typography>
      </MInview>

      <Stack spacing={2.5} direction="row">
        {['js', 'ts', 'nextjs', 'vite', 'figma'].map((platform) => (
          <MInview key={platform}>
            {platform === 'nextjs' ? (
              <SvgColor
                src={`${CONFIG.site.basePath}/assets/icons/platforms/ic-${platform}.svg`}
                sx={{ width: 24, height: 24 }}
              />
            ) : (
              <Box
                component="img"
                alt={platform}
                src={`${CONFIG.site.basePath}/assets/icons/platforms/ic-${platform}.svg`}
                sx={{ width: 24, height: 24 }}
              />
            )}
          </MInview>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Stack
      ref={scroll.elementRef}
      component="section"
      sx={{
        overflow: 'hidden',
        position: 'relative',
        [theme.breakpoints.up(mdKey)]: {
          minHeight: 760,
          height: '100vh',
          maxHeight: 1440,
          display: 'block',
          willChange: 'opacity',
          mt: 'calc(var(--layout-header-desktop-height) * -1)',
        },
        ...sx,
      }}
      {...other}
    >
      <Box
        component={m.div}
        style={{ opacity }}
        sx={{
          width: 1,
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
          transition: theme.transitions.create(['opacity']),
          [theme.breakpoints.up(mdKey)]: {
            height: 1,
            position: 'fixed',
            maxHeight: 'inherit',
          },
        }}
      >
        <Container
          component={MotionContainer}
          sx={{
            py: 3,
            gap: 5,
            zIndex: 9,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            [theme.breakpoints.up(mdKey)]: {
              flex: '1 1 auto',
              justifyContent: 'center',
              py: 'var(--layout-header-desktop-height)',
            },
          }}
        >
          <Stack spacing={3} sx={{ textAlign: 'center' }}>
            <m.div style={{ y: y1 }}>{renderHeading}</m.div>
            <m.div style={{ y: y2 }}>{renderText}</m.div>

          </Stack>
          <m.div style={{ y: y3 }}>{renderJoin}</m.div>

          <m.div style={{ y: y3 }}>{renderRatings}</m.div>
          {/*<m.div style={{ y: y4 }}>{renderButtons}</m.div>*/}
          {/*<m.div style={{ y: y5 }}>{renderIcons}</m.div>*/}
        </Container>

        <HeroBackground />
      </Box>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function MInview({ children, component = m.div }) {
  return (
    <Box component={component} variants={varFade({ distance: 24 }).inUp}>
      {children}
    </Box>
  );
}

// ----------------------------------------------------------------------

function useTransformY(value, distance) {
  const physics = {
    mass: 0.1,
    damping: 20,
    stiffness: 300,
    restDelta: 0.001,
  };

  return useSpring(useTransform(value, [0, 1], [0, distance]), physics);
}

function useScrollPercent() {
  const elementRef = useRef(null);

  const { scrollY } = useScroll();

  const [percent, setPercent] = useState(0);

  useMotionValueEvent(scrollY, 'change', (scrollHeight) => {
    let heroHeight = 0;

    if (elementRef.current) {
      heroHeight = elementRef.current.offsetHeight;
    }

    const scrollPercent = Math.floor((scrollHeight / heroHeight) * 100);

    if (scrollPercent >= 100) {
      setPercent(100);
    } else {
      setPercent(Math.floor(scrollPercent));
    }
  });

  return { elementRef, percent, scrollY };
}
